import { useRef, useCallback, useMemo, memo } from 'react';
import {
  CELL_WIDTH,
  ZIGZAG_EXTENSION,
  EVENT_BAR_HORIZONTAL_PADDING,
  EVENT_BAR_ROW_HEIGHT,
  EVENT_BAR_TOP_OFFSET
} from '../constants/layout';
import { ENTITY_TYPE, DRAG_ACTION } from '../constants/types';
import useDraggable from '../hooks/useDraggable';
import useTooltip from '../hooks/useTooltip';
import Tooltip from './Tooltip';
import './EventBar.scss';

/**
 * Calculate the number of days between two ISO date strings.
 * @param {string} startDateStr - Start date in YYYY-MM-DD format
 * @param {string} endDateStr - End date in YYYY-MM-DD format
 * @returns {number} Number of days between dates (inclusive would be +1)
 */
function daysBetween(startDateStr, endDateStr) {
  const [sy, sm, sd] = startDateStr.split('-').map(Number);
  const [ey, em, ed] = endDateStr.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

/**
 * Renders an event bar spanning multiple days in a month row.
 * Supports drag-to-move and resize handles for adjusting dates.
 * Handles multi-month events with continuation indicators (zigzag edges).
 * 
 * @param {Object} props
 * @param {Object} props.event - Event data
 * @param {string} props.event.id - Unique event identifier
 * @param {string} props.event.title - Event title
 * @param {string} props.event.startDate - Start date (YYYY-MM-DD)
 * @param {string} props.event.endDate - End date (YYYY-MM-DD)
 * @param {string} [props.event.color] - Background color
 * @param {string} [props.event.description] - Event description for tooltip
 * @param {boolean} [props.event.lockStart] - Whether start date is locked
 * @param {boolean} [props.event.lockEnd] - Whether end date is locked
 * @param {boolean} [props.event.lockDuration] - Whether duration is locked
 * @param {number} props.startColumn - Starting column index in the grid
 * @param {number} props.endColumn - Ending column index in the grid
 * @param {number} [props.row=0] - Vertical stacking row (0 = top)
 * @param {boolean} [props.continuesFromPrev=false] - Event continues from previous month
 * @param {boolean} [props.continuesToNext=false] - Event continues to next month
 * @param {boolean} [props.isHidden=false] - Hide bar (during drag of original)
 * @param {boolean} [props.isPreview=false] - Render as drag preview (no interactions)
 * @param {Function} [props.onClick] - Handler for click (event) => void
 * @param {Function} [props.onDragStart] - Handler for drag start (dragData, mouseEvent) => void
 * @param {Function} [props.onDragEnd] - Handler for drag end (mouseup anywhere) => void
 */
const EventBar = memo(function EventBar({ 
  event, 
  startColumn, 
  endColumn,
  row = 0, 
  continuesFromPrev = false,
  continuesToNext = false,
  isHidden = false,
  isPreview = false,
  onClick, 
  onDragStart,
  onDragEnd
}) {
  const barRef = useRef(null);
  const dragActionRef = useRef(null);
  
  // Adjust position and width for continuation indicators
  const leftOffset = continuesFromPrev ? -ZIGZAG_EXTENSION : EVENT_BAR_HORIZONTAL_PADDING;
  const totalWidth = (endColumn - startColumn + 1) * CELL_WIDTH - (EVENT_BAR_HORIZONTAL_PADDING * 2) + 
    (continuesFromPrev ? ZIGZAG_EXTENSION + EVENT_BAR_HORIZONTAL_PADDING : 0) + 
    (continuesToNext ? ZIGZAG_EXTENSION + EVENT_BAR_HORIZONTAL_PADDING : 0);
  
  const style = {
    left: `${startColumn * CELL_WIDTH + leftOffset}px`,
    width: `${totalWidth}px`,
    top: `${row * EVENT_BAR_ROW_HEIGHT + EVENT_BAR_TOP_OFFSET}px`,
    backgroundColor: event.color,
  };

  // Build drag data for a specific action
  const buildDragDataForAction = useCallback((action, e) => {
    // Block drag if read-only
    if (event.isReadOnly) {
      e.stopPropagation();
      return null;
    }
    
    // Block drag if locked
    if (action === DRAG_ACTION.MOVE && (event.lockStart || event.lockEnd)) {
      e.stopPropagation();
      return null;
    }
    if (action === DRAG_ACTION.RESIZE_START && event.lockStart) {
      e.stopPropagation();
      return null;
    }
    if (action === DRAG_ACTION.RESIZE_END && event.lockEnd) {
      e.stopPropagation();
      return null;
    }
    
    // Calculate duration for move operations
    const duration = daysBetween(event.startDate, event.endDate);
    
    // Calculate click offset (which day of the event was clicked)
    // Use elementsFromPoint to find the day cell under the click to avoid Safari zoom issues
    let clickOffset = 0;
    if (action === DRAG_ACTION.MOVE) {
      const elements = document.elementsFromPoint(e.clientX, e.clientY);
      
      // Find the day cell
      let dayCell = null;
      for (const el of elements) {
        if (el.hasAttribute('data-date')) {
          dayCell = el;
          break;
        }
        const parent = el.closest('[data-date]');
        if (parent) {
          dayCell = parent;
          break;
        }
      }
      
      if (dayCell) {
        const clickedDate = dayCell.getAttribute('data-date');
        // Calculate offset from event start to clicked date
        clickOffset = daysBetween(event.startDate, clickedDate);
        // Clamp to valid range
        clickOffset = Math.max(0, Math.min(clickOffset, duration));
      }
    }
    
    // Set drag data
    return {
      type: ENTITY_TYPE.EVENT,
      id: event.id,
      action,
      startDate: event.startDate,
      endDate: event.endDate,
      duration,
      clickOffset,
      lockStart: event.lockStart,
      lockEnd: event.lockEnd,
      lockDuration: event.lockDuration
    };
  }, [event]);

  // Memoize the buildDragData functions to prevent unnecessary hook re-renders
  const buildDragDataMove = useMemo(
    () => (e) => buildDragDataForAction(DRAG_ACTION.MOVE, e),
    [buildDragDataForAction]
  );
  const buildDragDataResizeStart = useMemo(
    () => (e) => buildDragDataForAction(DRAG_ACTION.RESIZE_START, e),
    [buildDragDataForAction]
  );
  const buildDragDataResizeEnd = useMemo(
    () => (e) => buildDragDataForAction(DRAG_ACTION.RESIZE_END, e),
    [buildDragDataForAction]
  );

  // Shared onDragStart handler
  const handleDragStart = useCallback((dragData, mouseEvent) => {
    dragActionRef.current = dragData.action;
    if (onDragStart) {
      onDragStart(dragData, mouseEvent);
    }
  }, [onDragStart]);

  // Use draggable hook for move action
  const { 
    isDragging: isDraggingMove, 
    wasDraggingRef: wasDraggingMoveRef,
    handlePotentialDrag: handlePotentialDragMove
  } = useDraggable({
    onDragStart: handleDragStart,
    onDragEnd,
    buildDragData: buildDragDataMove
  });

  // Use draggable hook for resize start action
  const { 
    isDragging: isDraggingResizeStart, 
    wasDraggingRef: wasDraggingResizeStartRef,
    handlePotentialDrag: handlePotentialDragResizeStart
  } = useDraggable({
    onDragStart: handleDragStart,
    onDragEnd,
    buildDragData: buildDragDataResizeStart
  });

  // Use draggable hook for resize end action
  const { 
    isDragging: isDraggingResizeEnd, 
    wasDraggingRef: wasDraggingResizeEndRef,
    handlePotentialDrag: handlePotentialDragResizeEnd
  } = useDraggable({
    onDragStart: handleDragStart,
    onDragEnd,
    buildDragData: buildDragDataResizeEnd
  });

  // Combine all isDragging states
  const isDragging = isDraggingMove || isDraggingResizeStart || isDraggingResizeEnd;

  const handleClick = (e) => {
    // Don't fire click if we just finished dragging
    if (wasDraggingMoveRef.current || wasDraggingResizeStartRef.current || wasDraggingResizeEndRef.current) {
      return;
    }
    e.stopPropagation();
    if (onClick) {
      onClick(event);
    }
  };



  // Use tooltip hook
  const { tooltipProps, handleMouseEnter, handleMouseLeave } = useTooltip({
    title: event.title,
    description: event.description,
    disabled: isDragging
  });

  // Check if any lock is active
  const isLocked = event.lockStart || event.lockEnd || event.lockDuration;

  const classNames = [
    'event-bar',
    isDragging ? 'event-bar--dragging' : '',
    isHidden ? 'event-bar--hidden' : '',
    isPreview ? 'event-bar--preview' : '',
    isLocked ? 'event-bar--locked' : '',
    continuesFromPrev ? 'event-bar--continues-from-prev' : '',
    continuesToNext ? 'event-bar--continues-to-next' : '',
    event.isFromSharedProject ? 'event-bar--shared' : ''
  ].filter(Boolean).join(' ');

  // Preview bars don't need interaction handlers
  if (isPreview) {
    return (
      <div 
        className={classNames}
        style={style}
      >
        <span className="event-bar__label">{event.title}</span>
      </div>
    );
  }

  return (
    <div 
      ref={barRef}
      className={classNames}
      style={style}
      onClick={handleClick}
      onMouseDown={handlePotentialDragMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Lock icon indicator - only show on final segment */}
      {isLocked && !continuesToNext && <span className="event-bar__lock-icon">🔒</span>}
      {/* Start resize handle - only show if event doesn't continue from previous month and isn't locked */}
      {!continuesFromPrev && !event.lockStart && (
        <div 
          className="event-bar__resize-handle event-bar__resize-handle--start"
          onMouseDown={handlePotentialDragResizeStart}
        />
      )}
      <span className="event-bar__label">{event.title}</span>
      {/* End resize handle - only show if event doesn't continue to next month and isn't locked */}
      {!continuesToNext && !event.lockEnd && (
        <div 
          className="event-bar__resize-handle event-bar__resize-handle--end"
          onMouseDown={handlePotentialDragResizeEnd}
        />
      )}
      {/* Tooltip component */}
      <Tooltip {...tooltipProps} />
    </div>
  );
});

export default EventBar;
